import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "./lib/apiFetch";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "./lib/supabase";
import { useSession } from "./App";
import FlowStrip from "./components/FlowStrip";
import MediaGallery from "./components/MediaGallery";

const SERVICE_TYPES = [
  ["hot_tub_feed", "Hot Tub Feed", "HT", "Hot Tub Feed"],
  ["ev_charger", "EV Charger", "EV", "EV Charger Install"],
  ["service_upgrade", "Service Upgrade", "SU", "Service Upgrade"],
  ["panel_swap", "Panel Swap", "PS", "Panel Swap"],
  ["generator_install", "Generator Install", "GI", "Generator Install"],
  ["generator_service", "Generator Service", "GS", "Generator Service"],
  ["solar_battery", "Solar / Battery", "SB", "Solar / Battery Install"],
  ["lighting", "Lighting", "LT", "Lighting"],
  ["new_build", "New Build", "NB", "New Build"],
  ["commercial", "Commercial", "CM", "Commercial"],
  ["other", "Other", "OT", "Other"],
].map(([key, label, icon, jobTypeLabel]) => ({ key, label, icon, jobTypeLabel }));

const REQUEST_BUCKETS = {
  active: { label: "Active", color: "#f59e0b", dot: "#f59e0b" },
  assessment_complete: { label: "Assessment Complete", color: "#8b5cf6", dot: "#8b5cf6" },
  quoted: { label: "Quoted", color: "#3b82f6", dot: "#3b82f6" },
  converted: { label: "Converted", color: "#10b981", dot: "#10b981" },
  archived: { label: "Archived", color: "#64748b", dot: "#64748b" },
};

const CLIENT_STATUS = {
  active: { label: "Client", color: "#10b981", dot: "#10b981" },
  lead: { label: "Lead", color: "#f59e0b", dot: "#f59e0b" },
  archived: { label: "Archived", color: "#64748b", dot: "#64748b" },
};

function todayLabel() {
  return new Date().toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" });
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const mins = Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function matchesSearch(values, term) {
  if (!term) return true;
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return values.some((value) => String(value || "").toLowerCase().includes(needle));
}

function requestLabel(request = {}) {
  const bucket = REQUEST_BUCKETS[request.lifecycleBucket] || REQUEST_BUCKETS.active;
  const rawStatus = String(request.status || "").replace(/_/g, " ").trim();
  return rawStatus ? `${bucket.label} · ${rawStatus}` : bucket.label;
}

function openExternal(url) {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
}

function ModalFrame({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ServiceTypeModal({ request, onSelect, onClose }) {
  return (
    <ModalFrame title="Start Assessment" onClose={onClose}>
      <div className="modal-client-name">{request?.clientName || "Request"}</div>
      {request?.address && <div className="modal-client-address">{request.address}</div>}
      <div className="service-grid">
        {SERVICE_TYPES.map((item) => (
          <button key={item.key} className="service-btn" onClick={() => onSelect(item)}>
            <div className="service-icon">{item.icon}</div>
            <div>{item.label}</div>
          </button>
        ))}
      </div>
    </ModalFrame>
  );
}

function NewAssessmentModal({ onSelect, onClose }) {
  const [step, setStep] = useState("info");
  const [draft, setDraft] = useState({ clientName: "", phone: "", email: "", address: "", description: "" });

  if (step === "type") {
    return (
      <ModalFrame title="Select Service Type" onClose={onClose}>
        <div className="modal-client-name">{draft.clientName || "New Client"}</div>
        {draft.address && <div className="modal-client-address">{draft.address}</div>}
        <div className="service-grid">
          {SERVICE_TYPES.map((item) => (
            <button key={item.key} className="service-btn" onClick={() => onSelect(item, { ...draft, source: "manual" })}>
              <div className="service-icon">{item.icon}</div>
              <div>{item.label}</div>
            </button>
          ))}
        </div>
      </ModalFrame>
    );
  }

  return (
    <ModalFrame title="New Assessment" onClose={onClose}>
      <div className="field"><label>Client Name *</label><input value={draft.clientName} onChange={(e) => setDraft((p) => ({ ...p, clientName: e.target.value }))} /></div>
      <div className="field"><label>Phone</label><input value={draft.phone} onChange={(e) => setDraft((p) => ({ ...p, phone: e.target.value }))} /></div>
      <div className="field"><label>Email</label><input value={draft.email} onChange={(e) => setDraft((p) => ({ ...p, email: e.target.value }))} /></div>
      <div className="field"><label>Address</label><input value={draft.address} onChange={(e) => setDraft((p) => ({ ...p, address: e.target.value }))} /></div>
      <div className="field"><label>Description</label><textarea rows={3} value={draft.description} onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))} /></div>
      <button className={`primary ${draft.clientName.trim() ? "" : "disabled"}`} disabled={!draft.clientName.trim()} onClick={() => setStep("type")}>Select Service Type</button>
    </ModalFrame>
  );
}

function StatsBar({ requests, clients, assessments }) {
  const activeRequests = requests.filter((r) => r.lifecycleBucket === "active").length;
  const completedRequests = requests.filter((r) => r.lifecycleBucket === "archived" || r.lifecycleBucket === "converted").length;
  return (
    <div className="stats-bar">
      {[
        [activeRequests, "Active Requests", "#a5ce3a"],
        [clients.length, "Clients", "#3b82f6"],
        [assessments.length, "Assessments", "#10b981"],
        [completedRequests, "Completed", "#64748b"],
      ].map(([value, label, color], i) => (
        <React.Fragment key={label}>
          <div className="stat-item"><div className="stat-num" style={{ color }}>{value}</div><div className="stat-label">{label}</div></div>
          {i < 3 && <div className="stat-divider" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function RequestCard({ request, assessmentCount, onOpen }) {
  const bucket = REQUEST_BUCKETS[request.lifecycleBucket] || REQUEST_BUCKETS.active;
  return (
    <div className="card clickable" onClick={() => onOpen(request)}>
      <div className="card-top">
        <div>
          <div className="card-title">{request.clientName}</div>
          {request.address && <div className="card-sub">{request.address}</div>}
        </div>
        <div className="small-muted">{timeAgo(request.updatedAt || request.createdAt)}</div>
      </div>
      {request.description && <div className="body-copy">{request.description}</div>}
      <div className="meta-row">
        <div className="status-row"><span className="dot" style={{ background: bucket.dot }} /><span style={{ color: bucket.color }}>{requestLabel(request)}</span></div>
        <div className="small-muted">{request.noteCount || 0} notes · {assessmentCount} assessments</div>
      </div>
      <div className="cta">View Request &#8250;</div>
    </div>
  );
}

function ClientCard({ client, requestCount }) {
  const status = CLIENT_STATUS[client.status] || CLIENT_STATUS.active;
  return (
    <div className="card">
      <div className="card-top">
        <div>
          <div className="card-title">{client.name}</div>
          {client.address && <div className="card-sub">{client.address}</div>}
        </div>
        {client.jobberWebUri && <button className="chip" onClick={() => openExternal(client.jobberWebUri)}>Open in Jobber</button>}
      </div>
      <div className="meta-row">
        <div className="status-row"><span className="dot" style={{ background: status.dot }} /><span style={{ color: status.color }}>{status.label}</span></div>
        <div className="small-muted">{requestCount} requests</div>
      </div>
      <div className="body-copy">{client.email || client.phone || "No email or phone cached yet."}</div>
    </div>
  );
}

function AssessmentCard({ assessment, onOpen }) {
  return (
    <div className="card clickable" onClick={() => onOpen(assessment)}>
      <div className="card-top">
        <div>
          <div className="card-title">{assessment.client_name || "Assessment"}</div>
          <div className="card-sub">{assessment.service_type || "General"}</div>
        </div>
        <div className="small-muted">{timeAgo(assessment.updated_at || assessment.created_at)}</div>
      </div>
      <div className="meta-row">
        <div className="status-row"><span className="dot" style={{ background: "#3b82f6" }} /><span style={{ color: "#3b82f6" }}>{String(assessment.workflow_state || "draft").replace(/_/g, " ")}</span></div>
      </div>
      <div className="cta">Open Assessment &#8250;</div>
    </div>
  );
}

function SyncStatusCard({ syncing, lastSyncAt, error, warning, requestCount, clientCount }) {
  const toneClass = syncing ? "sync-status syncing" : error ? "sync-status error" : warning ? "sync-status warning" : "sync-status";
  const headline = syncing
    ? "Syncing Jobber pipeline now"
    : error
      ? "Last sync needs attention"
      : warning
        ? "Using cached Jobber snapshot"
      : lastSyncAt
        ? `Last synced ${timeAgo(lastSyncAt)}`
        : "No data cached yet";
  const detail = syncing
    ? "Syncing from Jobber — results will update when complete."
    : error
      ? error
      : warning
        ? warning
      : lastSyncAt
        ? `Snapshot updated ${formatDateTime(lastSyncAt)} · Auto-updates every 15 min`
        : "Click Sync Jobber to pull the latest requests, or use Backfill for historical data.";

  return (
    <div className={toneClass}>
      <div>
        <div className="sync-title">{headline}</div>
        <div className="sync-copy">{detail}</div>
      </div>
      <div className="sync-metrics">
        <strong>{requestCount}</strong>
        <span>requests</span>
        <strong>{clientCount}</strong>
        <span>clients</span>
      </div>
    </div>
  );
}

function RequestDetail({ request, loading, onClose, onStartAssessment, noteDraft, setNoteDraft, onSaveNote, noteSaving, assessmentMap, noteStorageReady }) {
  const bucket = REQUEST_BUCKETS[request?.lifecycleBucket] || REQUEST_BUCKETS.active;
  const assessments = assessmentMap[request?.id] || [];
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Request Workspace</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {loading ? <div className="loading">Loading request details...</div> : (
          <>
            <div className="hero">
              <div>
                <div className="hero-title">{request?.clientName || "Request"}</div>
                <div className="card-sub">{request?.description || "No request title"}</div>
              </div>
              <div className="status-row"><span className="dot" style={{ background: bucket.dot }} /><span style={{ color: bucket.color }}>{requestLabel(request)}</span></div>
            </div>
            <div className="action-row">
              <button className="primary" onClick={() => onStartAssessment(request)}>Start Assessment</button>
              {request?.jobberWebUri && <button className="secondary" onClick={() => openExternal(request.jobberWebUri)}>Open Request in Jobber</button>}
              {request?.clientWebUri && <button className="secondary" onClick={() => openExternal(request.clientWebUri)}>Open Client in Jobber</button>}
            </div>
            <div className="detail-grid">
              <div className="panel">
                <div className="panel-title">Request Fields</div>
                {[
                  ["Created", formatDateTime(request?.createdAt)],
                  ["Updated", formatDateTime(request?.updatedAt)],
                  ["Phone", request?.phone || "—"],
                  ["Email", request?.email || "—"],
                  ["Source", request?.source || "—"],
                  ["Address", request?.address || "—"],
                ].map(([label, value]) => <div key={label} className="detail-row"><span>{label}</span><strong>{value}</strong></div>)}
              </div>
              <div className="panel">
                <div className="panel-title">Pipeline Links</div>
                {/* In-app flow-through — assessment / backend quote / takeoff / job / invoices */}
                {request?.id && <FlowStrip anchor={{ request: request.id }} style={{ marginBottom: 10 }} />}
                <div className="chip-wrap">
                  {(request?.quotes || []).map((quote) => <button key={quote.id} className="chip" onClick={() => openExternal(quote.jobberWebUri)}>Quote {quote.quoteNumber || quote.title || quote.id}</button>)}
                  {(request?.jobs || []).map((job) => <button key={job.id} className="chip" onClick={() => openExternal(job.jobberWebUri)}>Job {job.jobNumber || job.title || job.id}</button>)}
                  {(!request?.quotes?.length && !request?.jobs?.length) && <div className="small-muted">No linked Jobber quotes or jobs yet.</div>}
                </div>
                {assessments.length > 0 && (
                  <div className="stack">
                    {assessments.map((a) => <div key={a.id} className="detail-row"><span>{a.service_type || "assessment"}</span><strong>{String(a.workflow_state || "draft").replace(/_/g, " ")}</strong></div>)}
                  </div>
                )}
              </div>
            </div>
            <div className="detail-grid">
              <div className="panel">
                <div className="panel-title">ElecBid Notes</div>
                {!noteStorageReady && (
                  <div className="small-muted">
                    Internal note storage is not writable yet on this deployment, so ElecBid cannot save new request notes here until the pipeline tables are enabled.
                  </div>
                )}
                <textarea rows={4} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Save the full scope here so Jai-Man and the quote builder can reuse it later." disabled={!noteStorageReady} />
                <button className={`primary inline ${noteSaving || !noteDraft.trim() || !noteStorageReady ? "disabled" : ""}`} disabled={noteSaving || !noteDraft.trim() || !noteStorageReady} onClick={onSaveNote}>{noteSaving ? "Saving..." : "Save Note to ElecBid"}</button>
                <div className="stack">
                  {(request?.elecBidNotes || []).length ? request.elecBidNotes.map((note) => (
                    <div key={note.id} className="note">
                      <div className="small-muted">{note.createdBy || "ElecBid"} · {formatDateTime(note.createdAt)}</div>
                      <div className="body-copy">{note.noteText}</div>
                    </div>
                  )) : <div className="small-muted">No ElecBid notes saved yet.</div>}
                </div>
              </div>
              <div className="panel">
                <div className="panel-title">Jobber Notes</div>
                <div className="stack">
                  {(request?.jobberNotes || []).length ? request.jobberNotes.map((note) => (
                    <div key={note.id} className="note">
                      <div className="small-muted">{note.createdBy || "Jobber"} · {formatDateTime(note.createdAt)}</div>
                      <div className="body-copy">{note.message}</div>
                    </div>
                  )) : <div className="small-muted">No Jobber notes returned for this request.</div>}
                </div>
              </div>
            </div>
            {/* Photos on this request — assessment shots from our re-hosted archive */}
            {request?.id && (
              <div className="panel">
                <div className="panel-title">Photos</div>
                <MediaGallery scope={{ request: request.id }} thumbSize={72} emptyText="No photos on this request." />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { session } = useSession();
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("requests");
  const [requestFilter, setRequestFilter] = useState("active");
  const [requestSearch, setRequestSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [showNewAssessment, setShowNewAssessment] = useState(false);
  const [servicePickerRequest, setServicePickerRequest] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [syncWarning, setSyncWarning] = useState("");
  const [noteStorageReady, setNoteStorageReady] = useState(false);
  const [showBackfill, setShowBackfill] = useState(false);
  const [backfillDate, setBackfillDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().slice(0, 10);
  });
  const [backfillSyncing, setBackfillSyncing] = useState(false);

  const fetchAssessments = useCallback(async () => {
    const { data, error: sbError } = await supabase.from("smart_assessments").select("id,client_name,service_type,workflow_state,jobber_request_id,created_at,updated_at").order("updated_at", { ascending: false }).limit(100);
    if (sbError) throw sbError;
    return data || [];
  }, []);

  const loadPipeline = useCallback(async () => {
    const res = await apiFetch("/api/jobber", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "getPipelineSnapshot" }) });
    const data = await res.json();
    if (!res.ok || data.ok === false) throw new Error(data.error || "Failed to load request pipeline");
    return data;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pipeline, assessmentRows] = await Promise.all([loadPipeline(), fetchAssessments()]);
      setRequests(pipeline.requests || []);
      setClients(pipeline.clients || []);
      setAssessments(assessmentRows);
      setLastSyncAt(pipeline.syncedAt || null);
      setSyncWarning(pipeline.warning || "");
      setNoteStorageReady(!!pipeline.noteStorageReady);

      // Cache is empty and has never been synced — trigger a live sync to populate it.
      // This handles the first-run case and recoveries after cache is lost.
      if (!(pipeline.requests || []).length && !pipeline.syncedAt) {
        setSyncing(true);
        apiFetch("/api/jobber", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "syncPipeline" }) })
          .then((r) => r.json())
          .then((data) => {
            if (data.ok !== false) {
              setRequests(data.requests || []);
              setClients(data.clients || []);
              setLastSyncAt(data.syncedAt || null);
              setSyncWarning(data.warning || "");
              setNoteStorageReady(!!data.noteStorageReady);
            } else {
              setError(data.error || "Jobber sync failed — try the Sync Jobber button");
            }
          })
          .catch((err) => setError(err.message || "Jobber sync failed"))
          .finally(() => setSyncing(false));
      }
    } catch (err) {
      setError(err.message || "Failed to load pipeline data");
    } finally {
      setLoading(false);
    }
  }, [fetchAssessments, loadPipeline]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const assessmentMap = useMemo(() => {
    const map = {};
    for (const assessment of assessments) {
      if (!assessment.jobber_request_id) continue;
      if (!map[assessment.jobber_request_id]) map[assessment.jobber_request_id] = [];
      map[assessment.jobber_request_id].push(assessment);
    }
    return map;
  }, [assessments]);

  const clientRequestCounts = useMemo(() => {
    const map = {};
    for (const request of requests) {
      if (!request.clientId) continue;
      map[request.clientId] = (map[request.clientId] || 0) + 1;
    }
    return map;
  }, [requests]);

  const filteredRequests = useMemo(() => {
    const bucketed = requestFilter === "all"
      ? requests
      : requestFilter === "done"
        ? requests.filter((r) => r.lifecycleBucket === "archived" || r.lifecycleBucket === "converted")
        : requests.filter((r) => r.lifecycleBucket === requestFilter);

    return bucketed.filter((request) => matchesSearch([
      request.clientName,
      request.address,
      request.description,
      request.phone,
      request.email,
      request.status,
      request.source,
      request.id,
      ...(request.jobs || []).map((job) => `${job.jobNumber || ""} ${job.title || ""}`),
      ...(request.quotes || []).map((quote) => `${quote.quoteNumber || ""} ${quote.title || ""}`),
    ], requestSearch));
  }, [requestFilter, requestSearch, requests]);

  const filteredClients = useMemo(() => {
    return clients.filter((client) => matchesSearch([
      client.name,
      client.firstName,
      client.lastName,
      client.companyName,
      client.address,
      client.email,
      client.phone,
      client.status,
      ...(client.tags || []),
    ], clientSearch));
  }, [clientSearch, clients]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError(null);
    setSyncWarning("");
    try {
      const res = await apiFetch("/api/jobber", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "syncPipeline" }) });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Sync failed");
      setRequests(data.requests || []);
      setClients(data.clients || []);
      setAssessments(await fetchAssessments());
      setLastSyncAt(data.syncedAt || new Date().toISOString());
      setSyncWarning(data.warning || "");
      setNoteStorageReady(!!data.noteStorageReady);
    } catch (err) {
      setError(err.message || "Jobber sync failed");
    } finally {
      setSyncing(false);
    }
  }, [fetchAssessments]);

  const handleBackfill = useCallback(async () => {
    setBackfillSyncing(true);
    setError(null);
    try {
      const res = await apiFetch("/api/jobber", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "syncPipeline", backfill: true, sinceDate: backfillDate }) });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Backfill failed");
      setRequests(data.requests || []);
      setClients(data.clients || []);
      setLastSyncAt(data.syncedAt || new Date().toISOString());
      setSyncWarning(data.warning || "");
      setNoteStorageReady(!!data.noteStorageReady);
      setShowBackfill(false);
    } catch (err) {
      setError(err.message || "Backfill failed");
    } finally {
      setBackfillSyncing(false);
    }
  }, [backfillDate]);

  const openRequestDetail = useCallback(async (request) => {
    setSelectedRequest({ ...request, jobberNotes: [], elecBidNotes: [] });
    setDetailLoading(true);
    setNoteDraft("");
    try {
      const res = await apiFetch("/api/jobber", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "fetchRequestDetail", requestId: request.id }) });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Failed to load request detail");
      // data.request is null when Jobber is unreachable but Supabase notes loaded fine.
      setSelectedRequest((current) => ({
        ...(current || {}),
        ...(data.request || {}),
        jobberNotes: data.jobberNotes || [],
        elecBidNotes: data.elecBidNotes || [],
      }));
      setNoteStorageReady(!!data.noteStorageReady);
    } catch (err) {
      setError(err.message || "Failed to load request detail");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeRequestDetail = useCallback(() => {
    setSelectedRequest(null);
    setDetailLoading(false);
    setNoteDraft("");
  }, []);

  // ── /?request=<jobber gid> deep-link (the FlowStrip's Request chip) ──────
  // Opens the request workspace directly. The param is consumed (cleared from
  // the URL) so closing the modal doesn't bounce it back open. If the request
  // isn't in the cached list yet, a minimal {id} still works —
  // openRequestDetail fetches the full detail by id anyway.
  const consumedRequestParam = useRef(null);
  useEffect(() => {
    const requestId = searchParams.get("request");
    if (!requestId || loading || consumedRequestParam.current === requestId) return;
    consumedRequestParam.current = requestId;
    const match = requests.find((r) => r.id === requestId);
    openRequestDetail(match || { id: requestId, clientName: "", quotes: [], jobs: [] });
    const next = new URLSearchParams(searchParams);
    next.delete("request");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, loading, requests, openRequestDetail]);

  const handleSaveNote = useCallback(async () => {
    if (!selectedRequest?.id || !noteDraft.trim()) return;
    setNoteSaving(true);
    try {
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "saveRequestNote",
          requestId: selectedRequest.id,
          noteText: noteDraft.trim(),
          createdBy: session?.user?.email || "ElecBid",
        }),
      });
      const data = await res.json();
      if (!res.ok || data.ok === false) throw new Error(data.error || "Could not save note");
      setSelectedRequest((current) => current ? { ...current, elecBidNotes: [data.note, ...(current.elecBidNotes || [])] } : current);
      setRequests((current) => current.map((request) => request.id === selectedRequest.id ? { ...request, noteCount: (request.noteCount || 0) + 1 } : request));
      setNoteDraft("");
    } catch (err) {
      setError(err.message || "Could not save note");
    } finally {
      setNoteSaving(false);
    }
  }, [noteDraft, selectedRequest, session]);

  const startAssessment = useCallback((request) => {
    // Close the request workspace first — otherwise the service-type picker
    // (same z-index, earlier in DOM order) renders *behind* the still-open
    // detail modal and clicking "Start Assessment" looks like it does nothing.
    setSelectedRequest(null);
    setDetailLoading(false);
    setNoteDraft("");
    setServicePickerRequest(request);
  }, []);

  const handleServiceTypeSelect = useCallback((serviceType) => {
    const request = servicePickerRequest;
    setServicePickerRequest(null);
    if (!request) return;
    navigate("/assess/smart/new", {
      state: {
        serviceType: serviceType.key,
        jobTypeLabel: serviceType.jobTypeLabel,
        clientName: request.clientName || "",
        address: request.address || "",
        phone: request.phone || "",
        email: request.email || "",
        description: request.description || "",
        techId: session?.user?.email || "tech-001",
        techName: session?.user?.user_metadata?.full_name || "Tech",
        source: "jobber",
        jobberRequestId: request.id || null,
        // Carry the client identity too — without it the assessment only gets
        // jobber_client_id at quote-push (quote-brain linkClient), so the
        // Pipeline client-360 and client hub can't find it until then.
        jobberClientId: request.clientId || null,
      },
    });
  }, [navigate, servicePickerRequest, session]);

  const handleNewAssessmentSelect = useCallback((serviceType, clientData) => {
    setShowNewAssessment(false);
    navigate("/assess/smart/new", {
      state: {
        serviceType: serviceType.key,
        jobTypeLabel: serviceType.jobTypeLabel,
        clientName: clientData.clientName || "",
        address: clientData.address || "",
        phone: clientData.phone || "",
        email: clientData.email || "",
        description: clientData.description || "",
        techId: session?.user?.email || "tech-001",
        techName: session?.user?.user_metadata?.full_name || "Tech",
        source: "manual",
      },
    });
  }, [navigate, session]);

  return (
    <>
      <style>{HOME_CSS}</style>
      <div className="home-wrap">
        <div className="home-header">
          <div>
            <div className="company-name">Browning Electric</div>
            <div className="small-muted">{todayLabel()}</div>
          </div>
          <div className="header-actions">
            <button className="primary small" onClick={handleSync} disabled={syncing || backfillSyncing}>{syncing ? "Syncing..." : "Sync Jobber"}</button>
            <button className="secondary small" onClick={() => setShowBackfill((v) => !v)} disabled={syncing || backfillSyncing}>Backfill</button>
            <button className="secondary small" onClick={loadAll} disabled={loading}>{loading ? "..." : "Refresh"}</button>
          </div>
        </div>
        <div className="home-body">
          {showBackfill && (
            <div className="backfill-panel">
              <div className="backfill-title">Backfill from Jobber</div>
              <div className="backfill-body">Pull all requests created after this date (up to 1,000). For older data, use Jobber CSV export.</div>
              <div className="backfill-row">
                <input type="date" className="backfill-date" value={backfillDate} onChange={(e) => setBackfillDate(e.target.value)} />
                <button className="primary small" onClick={handleBackfill} disabled={backfillSyncing || !backfillDate}>{backfillSyncing ? "Backfilling..." : "Run Backfill"}</button>
                <button className="secondary small" onClick={() => setShowBackfill(false)}>Cancel</button>
              </div>
            </div>
          )}
          <StatsBar requests={requests} clients={clients} assessments={assessments} />
          <SyncStatusCard syncing={syncing} lastSyncAt={lastSyncAt} error={error} warning={syncWarning} requestCount={requests.length} clientCount={clients.length} />
          <button className="new-assessment" onClick={() => setShowNewAssessment(true)}>+ New Assessment</button>
          <div className="pill-row">
            {["requests", "clients", "assessments"].map((item) => (
              <button key={item} className={`pill ${tab === item ? "active" : ""}`} onClick={() => setTab(item)}>{item[0].toUpperCase() + item.slice(1)}</button>
            ))}
          </div>
          {tab === "requests" && (
            <div className="pill-row minor">
              {[
                ["active", "Active"],
                ["assessment_complete", "Assessment Complete"],
                ["quoted", "Quoted"],
                ["done", "Converted / Archived"],
                ["all", "All"],
              ].map(([key, label]) => (
                <button key={key} className={`pill ${requestFilter === key ? "active" : ""}`} onClick={() => setRequestFilter(key)}>{label}</button>
              ))}
            </div>
          )}
          {tab === "requests" && (
            <div className="field search-field">
              <input placeholder="Search request, client, address, phone, quote, or job #" value={requestSearch} onChange={(e) => setRequestSearch(e.target.value)} />
            </div>
          )}
          {tab === "clients" && (
            <div className="field search-field">
              <input placeholder="Search client, company, address, email, phone, or tag" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} />
            </div>
          )}
          {(loading || syncing) && <div className="loading"> {syncing ? "Syncing requests and clients from Jobber..." : "Loading ElecBid pipeline..."} </div>}
          {error && !loading && !syncing && <div className="error-box">{error}</div>}
          {!loading && !syncing && !error && tab === "requests" && (
            filteredRequests.length ? filteredRequests.map((request) => (
              <RequestCard key={request.id} request={request} assessmentCount={(assessmentMap[request.id] || []).length} onOpen={openRequestDetail} />
            )) : <div className="empty">{requestSearch.trim() ? "No requests match this search." : "No requests in this view right now."}</div>
          )}
          {!loading && !syncing && !error && tab === "clients" && (
            filteredClients.length ? filteredClients.map((client) => (
              <ClientCard key={client.id} client={client} requestCount={clientRequestCounts[client.id] || 0} />
            )) : <div className="empty">{clientSearch.trim() ? "No clients match this search." : "No clients synced yet."}</div>
          )}
          {!loading && !syncing && !error && tab === "assessments" && (
            assessments.length ? assessments.map((assessment) => (
              <AssessmentCard key={assessment.id} assessment={assessment} onOpen={(item) => navigate(`/assess/smart/${item.id}`)} />
            )) : <div className="empty">No assessments yet.</div>
          )}
        </div>
        {showNewAssessment && <NewAssessmentModal onSelect={handleNewAssessmentSelect} onClose={() => setShowNewAssessment(false)} />}
        {servicePickerRequest && <ServiceTypeModal request={servicePickerRequest} onSelect={handleServiceTypeSelect} onClose={() => setServicePickerRequest(null)} />}
        {selectedRequest && <RequestDetail request={selectedRequest} loading={detailLoading} onClose={closeRequestDetail} onStartAssessment={startAssessment} noteDraft={noteDraft} setNoteDraft={setNoteDraft} onSaveNote={handleSaveNote} noteSaving={noteSaving} assessmentMap={assessmentMap} noteStorageReady={noteStorageReady} />}
      </div>
    </>
  );
}

const HOME_CSS = `
  .home-wrap{max-width:920px;margin:0 auto;min-height:100vh;background:var(--bg,#0d0f12);display:flex;flex-direction:column}
  .home-header{display:flex;justify-content:space-between;align-items:center;padding:18px 16px 14px;background:var(--surface,#161a20);border-bottom:1px solid var(--border,#2a3140);position:sticky;top:0;z-index:10}
  .header-actions{display:flex;gap:8px}
  .company-name{font-size:10px;font-weight:400;color:#888;letter-spacing:.02em}
  .home-body{padding:16px;flex:1}
  .stats-bar,.card,.panel,.modal-content{background:var(--surface,#161a20);border:1px solid var(--border,#2a3140);border-radius:12px}
  .stats-bar{display:flex;overflow:hidden;margin-bottom:12px}
  .stat-item{flex:1;padding:12px 8px;text-align:center}.stat-num{font-size:22px;font-weight:700;font-family:'Bebas Neue',sans-serif}.stat-label{font-size:10px;color:var(--text-muted,#8892a4);margin-top:4px;text-transform:uppercase;letter-spacing:.05em}.stat-divider{width:1px;background:var(--border,#2a3140);margin:10px 0}
  .sync-status{display:flex;justify-content:space-between;gap:12px;align-items:center;background:rgba(59,130,246,.08);border:1px solid rgba(59,130,246,.28);border-radius:12px;padding:12px 14px;margin-bottom:12px}.sync-status.syncing{border-color:rgba(165,206,58,.35);background:rgba(165,206,58,.08)}.sync-status.warning{border-color:rgba(245,158,11,.35);background:rgba(245,158,11,.08)}.sync-status.error{border-color:rgba(239,68,68,.35);background:rgba(239,68,68,.08)}.sync-title{font-size:14px;font-weight:700;color:var(--text,#e8eaf0)}.sync-copy{font-size:12px;color:var(--text-muted,#8892a4);margin-top:4px;line-height:1.4}.sync-metrics{display:grid;grid-template-columns:auto auto;gap:2px 10px;align-items:center;text-align:right;white-space:nowrap}.sync-metrics strong{font-size:16px;color:var(--text,#e8eaf0)}.sync-metrics span{font-size:11px;color:var(--text-muted,#8892a4);text-transform:uppercase;letter-spacing:.05em}
  .new-assessment{width:100%;padding:14px;border-radius:12px;border:2px dashed var(--accent,#a5ce3a);background:rgba(165,206,58,.08);color:var(--accent,#a5ce3a);font-size:15px;font-weight:700;cursor:pointer;margin-bottom:16px}
  .pill-row{display:flex;gap:8px;overflow:auto;margin-bottom:14px}.pill{padding:6px 14px;border-radius:20px;border:1.5px solid var(--border,#2a3140);background:transparent;color:var(--text-muted,#8892a4);cursor:pointer;white-space:nowrap}.pill.active{border-color:var(--accent,#a5ce3a);background:rgba(165,206,58,.12);color:var(--accent,#a5ce3a)}
  .card{padding:14px;margin-bottom:10px;transition:border-color 0.15s}.clickable{cursor:pointer;transition:border-color 0.15s,background 0.15s}.clickable:hover{border-color:rgba(165,206,58,.35);background:rgba(22,26,32,.85)}.clickable:active{background:var(--surface2,#1e2328)}.card-top,.meta-row,.detail-row,.modal-header,.hero,.action-row{display:flex;justify-content:space-between;gap:10px}.card-top,.meta-row{align-items:flex-start}.meta-row{align-items:center}.card-title{font-size:15px;font-weight:600;color:var(--text,#e8eaf0)}.card-sub,.small-muted,.body-copy{font-size:12px;color:var(--text-muted,#8892a4)}.body-copy{line-height:1.5;margin-top:8px;white-space:pre-wrap}.cta{margin-top:10px;padding-top:10px;border-top:1px solid var(--border,#2a3140);font-size:13px;font-weight:600;color:var(--accent,#a5ce3a);text-align:right}
  .status-row{display:flex;align-items:center;gap:6px;flex-wrap:wrap}.dot{width:7px;height:7px;border-radius:50%}
  .primary,.secondary,.chip{border-radius:10px;padding:10px 12px;font-size:13px;border:1px solid var(--border,#2a3140);cursor:pointer;font-family:inherit;transition:opacity 0.15s,transform 0.1s,box-shadow 0.15s;outline:none}.primary{background:var(--accent,#a5ce3a);border-color:var(--accent,#a5ce3a);color:#111;font-weight:700}.primary:hover:not(:disabled){box-shadow:0 2px 10px rgba(165,206,58,.35);transform:translateY(-1px)}.secondary,.chip{background:var(--surface2,#1e2328);color:var(--text,#e8eaf0)}.secondary:hover:not(:disabled){border-color:rgba(165,206,58,.4);color:var(--accent,#a5ce3a)}.small{padding:6px 12px}.disabled{opacity:.55;cursor:not-allowed}
  .backfill-panel{background:rgba(165,206,58,.07);border:1.5px solid rgba(165,206,58,.3);border-radius:12px;padding:14px;margin-bottom:12px}.backfill-title{font-size:13px;font-weight:700;color:var(--accent,#a5ce3a);margin-bottom:4px}.backfill-body{font-size:12px;color:var(--text-muted,#8892a4);margin-bottom:10px}.backfill-row{display:flex;gap:8px;align-items:center;flex-wrap:wrap}.backfill-date{background:var(--surface2,#1e2328);border:1px solid var(--border,#2a3140);border-radius:8px;color:var(--text,#e8eaf0);padding:6px 10px;font:inherit;flex:1;min-width:140px}
  .search-field{margin:-2px 0 14px}
  .loading,.empty,.error-box{padding:28px 16px;text-align:center;color:var(--text-muted,#8892a4)}.error-box{color:#ef4444}
  .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.72);display:flex;align-items:flex-end;justify-content:center;z-index:100}.modal-content{width:100%;max-width:920px;max-height:90vh;overflow:auto;padding:0 16px 24px;border-radius:20px 20px 0 0}.detail{max-height:100vh;border-radius:0}.modal-header{align-items:center;padding:18px 0 12px;border-bottom:1px solid var(--border,#2a3140);margin-bottom:16px}.modal-title{font-size:17px;font-weight:700;flex:1}.modal-close{background:none;border:none;color:var(--text-muted,#8892a4);font-size:18px;cursor:pointer;padding:4px 8px}
  .modal-client-name{font-size:15px;font-weight:600}.modal-client-address{font-size:12px;color:var(--text-muted,#8892a4);margin:2px 0 16px}.service-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px}.service-btn{background:var(--surface2,#1e2328);border:1.5px solid var(--border,#2a3140);border-radius:12px;padding:20px 12px;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:6px;color:var(--text,#e8eaf0)}.service-icon{font-size:16px;font-weight:800;color:var(--accent,#a5ce3a);font-family:'Bebas Neue',sans-serif}
  .field{display:flex;flex-direction:column;gap:4px;margin-bottom:12px}.field label,.panel-title{font-size:12px;font-weight:600;color:var(--text-muted,#8892a4);text-transform:uppercase;letter-spacing:.06em}.field input,.field textarea,.panel textarea{background:var(--surface2,#1e2328);border:1px solid var(--border,#2a3140);border-radius:8px;color:var(--text,#e8eaf0);padding:10px 12px;font:inherit}
  .hero-title{font-size:20px;font-weight:700;color:var(--text,#e8eaf0)}.action-row{flex-wrap:wrap;margin:0 0 16px}.detail-grid{display:grid;grid-template-columns:1fr;gap:12px;margin-bottom:12px}.panel{padding:12px}.detail-row{padding:6px 0;border-bottom:1px solid rgba(42,49,64,.55);font-size:13px}.detail-row:last-child{border-bottom:none}.detail-row span{color:var(--text-muted,#8892a4)}.detail-row strong{color:var(--text,#e8eaf0);text-align:right}.chip-wrap,.stack{display:flex;flex-wrap:wrap;gap:8px;margin-top:8px}.stack{flex-direction:column}.inline{margin-top:10px}.note{background:rgba(13,15,18,.45);border:1px solid rgba(42,49,64,.65);border-radius:10px;padding:10px}
  @media(max-width:640px){.sync-status{flex-direction:column;align-items:flex-start}.sync-metrics{text-align:left}}
  @media(min-width:768px){.detail-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.modal-content{margin:24px;max-height:calc(100vh - 48px);border-radius:18px}.detail{border-radius:18px}}
`;
